/// <amd-module name="VC/Search/SearchProvider" />
import React = require("react");
import { ObservableValue } from "VSS/Core/Observable";
import { Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled, isFeatureEnabled } from "VSS/Platform/Feature";
import { ISearchInput, ISearchQuery, ISearchProvider, ISearchResult, ISearchStatus } from "VSS/Platform/Search";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ITfsSearchQuery } from "Tfs/Platform/Search";
import { IVCNavigationService } from "VC/Common/Navigation";
import { VersionControlSearchHelpComponent } from "./SearchHelp";
import { VersionControlSearchResultComponent } from "./SearchResult";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { ISearchFilter } from "VSS/Features/Search/SearchHelp";

const InfiniteScrollFeatureFlag: string = "WebAccess.Search.Code.InfiniteScroll";
const DisableAggregationsFeatureFlag: string = "WebAccess.Search.Code.DisableAggregations";

interface IVCSearchQuery extends ITfsSearchQuery {
    filters: {
        ProjectFilters?: string[];

        RepositoryFilters?: string[];

        BranchFilters?: string[];

        PathFilters?: string[];
    };
}

class VersionControlSearchProvider extends VssService implements ISearchProvider {
    public execute(searchQuery: ISearchQuery): ISearchStatus {
        return { searchQuery: searchQuery, searchResult: new ObservableValue<ISearchResult | undefined>(undefined) };
    }

    public getQuery(searchQuery: ISearchQuery): IVCSearchQuery {

        const vcSearchQuery: IVCSearchQuery = Object.assign({
            type: "code",
            filters: {}
        }, searchQuery);

        if (isFeatureFlagEnabled(this.pageContext, InfiniteScrollFeatureFlag, false)) {
            vcSearchQuery.pageSize = 25;
        }

        if (isFeatureFlagEnabled(this.pageContext, DisableAggregationsFeatureFlag, false)) {
            vcSearchQuery.includeFacets = false;
        }

        // Determine which projects we should be searching.
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            vcSearchQuery.filters.ProjectFilters = [tfsPageData.project.name];

            // Determine which repositories we should be searching.
            const vcNavigationService = this.pageContext.getService<IVCNavigationService>("IVCNavigationService");
            const vcNavigationData = vcNavigationService.getData();

            if (vcNavigationData) {
                const repo: string = vcNavigationData.defaultRepoIsGit
                    ? vcNavigationData.defaultGitRepoName : `$/${tfsPageData.project.name}`;
                vcSearchQuery.filters.RepositoryFilters = [repo];
            }
        }
        return vcSearchQuery;
    }

    public render(searchStatus: ISearchStatus): JSX.Element {
        return <VersionControlSearchResultComponent searchStatus={searchStatus} />;
    }

    public help(searchInput: ISearchInput): JSX.Element {
        return <VersionControlSearchHelpComponent searchInput={searchInput} onItemActivated={this._notifyFilterActivated} />;
    }

    public performAction(query: IVCSearchQuery): Promise<boolean> {
        return Promise.resolve(false);
    }

    private _notifyFilterActivated = (filter: ISearchFilter): void => {
        const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("L1SearchBox", "Code.SearchHelpFilterActivated", {
            filterText: filter && filter.text,
            entity: "Code"
        });
    }
}

Services.add("versioncontrol-search-provider", { serviceFactory: VersionControlSearchProvider });
